import React from 'react';
import { useTranslation } from 'react-i18next';
import Chart from 'react-apexcharts';
import { formatCompactNumber, formatAmount } from '../../Shared/Formatters';

/**
 * Dynamic Comparison widget renderer.
 *
 * Consumes the shape produced by analyticsService.compare():
 *   data: { entityA: { label, series: [{_id, sales|...}] }, entityB: same }
 *
 * Plots two line series side-by-side on a shared x-axis.
 */
export default function DynamicComparisonWidget({ data, widget }) {
    const { t } = useTranslation();
    const a = data?.data?.entityA;
    const b = data?.data?.entityB;
    if (!a || !b) {
        return <div style={{ padding: 20 }} className="v3-body">{t('Comparison data unavailable.')}</div>;
    }

    const metric = data?.meta?.metric || 'sales';
    const metricKey = METRIC_KEY[metric] || 'sales';

    const seriesA = (a.series || []).map(r => ({ x: r._id, y: Number(r[metricKey] || 0) }));
    const seriesB = (b.series || []).map(r => ({ x: r._id, y: Number(r[metricKey] || 0) }));

    if (!seriesA.length && !seriesB.length) {
        return <div style={{ padding: 20 }} className="v3-body">{t('No data for selected entities / range.')}</div>;
    }

    const series = [
        { name: a.label || t('Entity A'), data: seriesA },
        { name: b.label || t('Entity B'), data: seriesB },
    ];

    const isDark = document.documentElement.getAttribute('data-theme') !== 'light';
    const options = {
        chart: { type: 'line', toolbar: { show: false }, fontFamily: 'Inter, system-ui', background: 'transparent' },
        colors: ['#3B82F6', '#F59E0B'],
        stroke: { width: 2, curve: 'smooth' },
        grid: { borderColor: 'var(--v3-chart-grid)', strokeDashArray: 4 },
        xaxis: {
            type: 'category',
            labels: { style: { colors: 'var(--v3-text-muted)', fontSize: '10px' } },
        },
        yaxis: {
            labels: {
                style: { colors: 'var(--v3-text-muted)', fontSize: '10px', fontFamily: 'JetBrains Mono, monospace' },
                formatter: formatCompactNumber,
            },
        },
        tooltip: {
            theme: isDark ? 'dark' : 'light',
            y: { formatter: (val) => formatAmount(val) },
        },
        dataLabels: { enabled: false },
        legend: { show: true, fontSize: '10px', labels: { colors: 'var(--v3-text-muted)' } },
    };

    return (
        <div style={{ padding: '4px 10px 8px', height: '100%' }}>
            <Chart type="line" options={options} series={series} height="100%" />
        </div>
    );
}

const METRIC_KEY = {
    sales: 'sales',
    revenue: 'revenue_share_amount',
    transactions: 'trans_count',
};
